import { every, filter, flatMap, groupBy, includes, isEmpty, keyBy, map, orderBy, some, uniq, values } from 'lodash';

import { SlideChannelNormalizationResultSummary } from 'api/slideMultiplexChannelNormalizations';
import { hasCompleteChannelNormalizationResults } from 'components/StudyDashboard/QCMarkers/normalizationInsights';
import { ModelInference } from 'interfaces/calculateFeatures';
import { MapArtifactToSlides } from 'interfaces/jobs/basic';

export interface MarkerNormalizationGapGroup {
  markerIds: string[];
  slideIds: string[];
}

export interface MarkerNormalizationAvailability {
  normalizedMarkerIds: string[];
  gapGroups: MarkerNormalizationGapGroup[];
}

const isMarkerNormalizedOnSlide = (
  slideSummary: SlideChannelNormalizationResultSummary | undefined,
  markerId: string
) => {
  const markerChannels = filter(values(slideSummary?.normalizationsSummary || {}), { markerType: markerId });

  return (
    !isEmpty(markerChannels) &&
    every(markerChannels, (channel) => hasCompleteChannelNormalizationResults(channel.channelNormalizationResults))
  );
};

/** Markers are matched by marker type, never channel index, because channel order differs across slides. */
export const getMarkerNormalizationAvailability = ({
  normalizationSummaries,
  slideIds,
  markerIds,
}: {
  normalizationSummaries?: SlideChannelNormalizationResultSummary[];
  slideIds: string[];
  markerIds: string[];
}): MarkerNormalizationAvailability => {
  const summaryBySlideId = keyBy(normalizationSummaries || [], 'slideId');
  const slideGaps = map(slideIds, (slideId) => ({
    slideId,
    markerIds: filter(markerIds, (markerId) => !isMarkerNormalizedOnSlide(summaryBySlideId[slideId], markerId)),
  }));
  const slideGapsWithMissingMarkers = filter(
    slideGaps,
    ({ markerIds: missingMarkerIds }) => !isEmpty(missingMarkerIds)
  );
  const gapGroups = orderBy(
    map(
      groupBy(slideGapsWithMissingMarkers, ({ markerIds: missingMarkerIds }) => missingMarkerIds.join('|')),
      (groupSlideGaps) => ({
        markerIds: groupSlideGaps[0].markerIds,
        slideIds: map(groupSlideGaps, 'slideId'),
      })
    ),
    [({ slideIds: groupSlideIds }) => groupSlideIds.length, ({ markerIds: groupMarkerIds }) => groupMarkerIds.length],
    ['desc', 'desc']
  );

  return {
    normalizedMarkerIds: filter(
      markerIds,
      (markerId) =>
        !some(slideGapsWithMissingMarkers, ({ markerIds: missingMarkerIds }) => includes(missingMarkerIds, markerId))
    ),
    gapGroups,
  };
};

export const getSlideIdsWithArtifactResults = (inferenceArtifacts?: ModelInference[]): string[] => {
  return uniq(
    flatMap(inferenceArtifacts || [], (inferenceArtifact) =>
      flatMap(inferenceArtifact.orchestrations || [], (orchestration) => orchestration.slideIds || [])
    )
  );
};

export const getMissingArtifactResultSlideIds = ({
  selectedSlideIds,
  inferenceArtifacts,
}: {
  selectedSlideIds: string[];
  inferenceArtifacts?: ModelInference[];
}) => {
  const slideIdsWithArtifactResults = getSlideIdsWithArtifactResults(inferenceArtifacts);

  return filter(selectedSlideIds, (slideId) => !includes(slideIdsWithArtifactResults, slideId));
};

export const getMissingSelectedArtifactResultSlideIds = ({
  selectedSlideIds,
  inputArtifactUrlPatterns,
}: {
  selectedSlideIds: string[];
  inputArtifactUrlPatterns?: MapArtifactToSlides[];
}) => {
  const selectedArtifactSlideIds = uniq(flatMap(inputArtifactUrlPatterns || [], 'slideIds'));

  return filter(selectedSlideIds, (slideId) => !includes(selectedArtifactSlideIds, slideId));
};
